/**
 * Shell root: boot loading page → (boot settled) → real UI in one switch.
 * Pure kernel component with zero plugin dependencies — before settled it may
 * only rely on itself (the fail-loud presentation must not depend on the
 * system whose failure it reports; the status/signal stores are kernel-own,
 * shell self-sufficiency rule); the real UI is produced by the
 * app-shell entry once every entry is active. A failed boot keeps the
 * loading page, lists the per-entry fiber states and the sweep report (fail
 * loud, no partial UI).
 */
import { useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import type { KernelSignal, LoaderStatus } from './loader-status.ts'
import css from './AppRoot.module.css'

/** User-facing copy for the shell-owned loading and failure surface. */
export interface AppRootPresentation {
  /** Product wordmark displayed while the plugin graph starts. */
  wordmark: string
  /** Progress label displayed before the boot settles. */
  loadingLabel: string
  /** Heading displayed when the boot cannot activate every entry. */
  failureTitle: string
  /** Product-safe recovery guidance displayed after a boot failure. */
  failureHint?: string
  /** Whether internal plugin identifiers and loader errors are visible. */
  diagnosticsVisible: boolean
}

/** AppRoot props: settled signal, fiber-state projection feed, boot failure report, deferred real-UI factory. */
export interface AppRootProps {
  /** True once the boot chain settled (loader quiesced + all entries ACTIVE); the boot closure flips it. */
  settled: KernelSignal<boolean>
  /** Per-entry fiber-state projection store (drives loading/failed rendering). */
  status: KernelSignal<LoaderStatus>
  /** Boot failure report (the settle rejection message); undefined while loading or after success. */
  error: KernelSignal<string | undefined>
  /** Builds the real UI; called only after settled. */
  renderApp: () => ReactNode
  /** Optional product-specific loading copy; the generic Harness copy remains the default. */
  presentation?: AppRootPresentation
}

const DEFAULT_PRESENTATION: AppRootPresentation = {
  wordmark: 'HARNESS',
  loadingLabel: 'Loading plugins…',
  failureTitle: 'Failed to load plugins',
  diagnosticsVisible: true,
}

/** Boot gate: loading page until the boot settles; failures stay here. */
export function AppRoot(props: AppRootProps) {
  const settled = useSyncExternalStore(props.settled.subscribe, props.settled.getSnapshot)
  const status = useSyncExternalStore(props.status.subscribe, props.status.getSnapshot)
  const error = useSyncExternalStore(props.error.subscribe, props.error.getSnapshot)
  const failed = Object.entries(status).filter(([, s]) => s === 'failed')
  const presentation = props.presentation ?? DEFAULT_PRESENTATION

  if (settled) return <>{props.renderApp()}</>

  const loud = error !== undefined || failed.length > 0

  return (
    <div className={css.boot} data-web-boot-surface="">
      <div className={css.card}>
        <div className={css.wordmark}>{presentation.wordmark}</div>
        {!loud
          ? (
            <>
              <div className={css.spinner} />
              <div className={css.hint}>{presentation.loadingLabel}</div>
            </>
          )
          : (
            <div className={css.failed}>
              <div className={css.failedTitle}>{presentation.failureTitle}</div>
              {presentation.failureHint !== undefined && <div className={css.failedItem}>{presentation.failureHint}</div>}
              {presentation.diagnosticsVisible && failed.map(([id]) => <div key={id} className={css.failedItem}>{id}</div>)}
              {presentation.diagnosticsVisible && error !== undefined && <div className={css.failedItem}>{error}</div>}
            </div>
          )}
      </div>
    </div>
  )
}
